import { derinApiClient } from "./setup";
import {
  Collection,
  CollectionReport,
  DeleteCollectionResult,
  DerinApiResponse,
} from "./interface";

/** Everything the Collections tab needs for one organization, in a single call. */
export const getCollectionsByOrganization = async (
  organizationId: string
): Promise<Collection[]> => {
  const response = await derinApiClient().get<DerinApiResponse<Collection[]>>(
    `/collection/organization/${organizationId}`
  );
  return response.data.data;
};

export interface CreateCollectionVariantRequest {
  label: string;
  survey_content: string;
}

export interface CreateCollectionRequest {
  name: string;
  description: string;
  organization_id: string;
  primary_variant: CreateCollectionVariantRequest;
  additional_variants?: CreateCollectionVariantRequest[];
}

export interface CreateCollectionResult {
  collectionId: string;
  createdVariants: string[];
  /** Set when the collection and its primary exist but an extra variant failed. */
  failedVariant?: { label: string; error: string };
}

export const createCollection = async (
  data: CreateCollectionRequest
): Promise<CreateCollectionResult> => {
  const response = await derinApiClient().post<
    DerinApiResponse<CreateCollectionResult>
  >("/collection", data);
  return response.data.data;
};

/**
 * Creates a collection from a configuration file alone.
 *
 * Unlike `createCollection`, the file carries no survey content — it names surveys by
 * their `original_id`, so every one of them has to be uploaded to this organization
 * already. The collection's name, description and variant labels all come from the file.
 */
export const createCollectionFromConfig = async (
  organizationId: string,
  configYaml: string,
  configFileName: string
): Promise<Collection> => {
  const response = await derinApiClient().post<DerinApiResponse<Collection>>(
    "/collection/from-config",
    {
      organization_id: organizationId,
      config_yaml: configYaml,
      config_file_name: configFileName,
    }
  );
  return response.data.data;
};

export const addCollectionVariant = async (
  collectionId: string,
  organizationId: string,
  data: CreateCollectionVariantRequest
): Promise<Collection> => {
  const response = await derinApiClient().post<DerinApiResponse<Collection>>(
    `/collection/${collectionId}/variants`,
    data,
    { params: { organization_id: organizationId } }
  );
  return response.data.data;
};

/** New version of one variant. Only that variant's version changes. */
export const uploadVariantVersion = async (
  collectionId: string,
  organizationId: string,
  surveyOriginalId: string,
  surveyContent: string
): Promise<Collection> => {
  const response = await derinApiClient().post<DerinApiResponse<Collection>>(
    `/collection/${collectionId}/variants/${encodeURIComponent(surveyOriginalId)}/versions`,
    { survey_content: surveyContent },
    { params: { organization_id: organizationId } }
  );
  return response.data.data;
};

export const updateVariantLabel = async (
  collectionId: string,
  organizationId: string,
  surveyOriginalId: string,
  label: string
): Promise<Collection> => {
  const response = await derinApiClient().patch<DerinApiResponse<Collection>>(
    `/collection/${collectionId}/variants/${encodeURIComponent(surveyOriginalId)}`,
    { label },
    { params: { organization_id: organizationId } }
  );
  return response.data.data;
};

/** Unlinks the variant. The survey itself is not deleted. */
export const removeCollectionVariant = async (
  collectionId: string,
  organizationId: string,
  surveyOriginalId: string
): Promise<Collection> => {
  const response = await derinApiClient().delete<DerinApiResponse<Collection>>(
    `/collection/${collectionId}/variants/${encodeURIComponent(surveyOriginalId)}`,
    { params: { organization_id: organizationId } }
  );
  return response.data.data;
};

/**
 * Soft-deletes a whole collection: the collection, every survey linked to it, and all
 * their participantsurvey records are marked deleted, so none of them appear in the
 * client app again. Nothing is physically removed. (86bc66gkv)
 */
export const deleteCollection = async (
  collectionId: string,
  organizationId: string
): Promise<DeleteCollectionResult> => {
  const response = await derinApiClient().delete<
    DerinApiResponse<DeleteCollectionResult>
  >(`/collection/${collectionId}`, {
    params: { organization_id: organizationId },
  });
  return response.data.data;
};

/** Makes an existing variant the collection's primary one. */
export const setPrimaryVariant = async (
  collectionId: string,
  organizationId: string,
  surveyOriginalId: string
): Promise<Collection> => {
  const response = await derinApiClient().patch<DerinApiResponse<Collection>>(
    `/collection/${collectionId}`,
    { primary_survey_original_id: surveyOriginalId },
    { params: { organization_id: organizationId } }
  );
  return response.data.data;
};

/**
 * Opens or closes early access to Results and Chat with Data for this collection.
 *
 * Goes to the same PATCH as `setPrimaryVariant`, but the API stores this one itself
 * instead of forwarding it to the Survey API, so a toggle does not rewrite the
 * collection's configuration or touch its variants.
 */
export const setCollectionEarlyAccess = async (
  collectionId: string,
  organizationId: string,
  enabled: boolean
): Promise<Collection> => {
  const response = await derinApiClient().patch<DerinApiResponse<Collection>>(
    `/collection/${collectionId}`,
    { early_access_enabled: enabled },
    { params: { organization_id: organizationId } }
  );
  return response.data.data;
};

/**
 * One collection with everything the accordion shows: its variants and their versions,
 * respondent counts and status, plus the applied file names and the published reports.
 *
 * The listing deliberately stops short of this. Fetching it per card cost one Survey API
 * call per collection on every page load, for rows nobody had opened yet.
 */
export const getCollectionDetail = async (
  collectionId: string,
  organizationId: string
): Promise<Collection> => {
  const response = await derinApiClient().get<DerinApiResponse<Collection>>(
    `/collection/${collectionId}/detail`,
    { params: { organization_id: organizationId } }
  );
  return response.data.data;
};

/**
 * The configuration currently applied, as YAML. The applied file name is not persisted by
 * the Survey API, so downloading this is how a researcher sees what is really in effect.
 */
export const getCollectionConfig = async (
  collectionId: string,
  organizationId: string
): Promise<{ configYaml: string; configVersion: number }> => {
  const response = await derinApiClient().get<
    DerinApiResponse<{ configYaml: string; configVersion: number }>
  >(`/collection/${collectionId}/config`, {
    params: { organization_id: organizationId },
  });
  return response.data.data;
};

/**
 * Applies the collection's configuration file — the one that controls how surveys and
 * questions are presented to the client. Not versioned: it replaces the previous file.
 */
export const uploadCollectionConfig = async (
  collectionId: string,
  organizationId: string,
  configYaml: string,
  configFileName: string
): Promise<Collection> => {
  const response = await derinApiClient().put<DerinApiResponse<Collection>>(
    `/collection/${collectionId}/config`,
    { config_yaml: configYaml, config_file_name: configFileName },
    { params: { organization_id: organizationId } }
  );
  return response.data.data;
};

/**
 * The About the Study file as it was uploaded — the study's goals, methodology and
 * context, which the client app renders as its own tab.
 *
 * Unlike the configuration file, this one is stored by the Derin API rather than
 * forwarded to the Survey API, so the original bytes and the file name come back as
 * they went up.
 */
export const getCollectionAbout = async (
  collectionId: string,
  organizationId: string
): Promise<{ aboutYaml: string; aboutFileName: string | null; updatedAt: string }> => {
  const response = await derinApiClient().get<
    DerinApiResponse<{ aboutYaml: string; aboutFileName: string | null; updatedAt: string }>
  >(`/collection/${collectionId}/about-file`, {
    params: { organization_id: organizationId },
  });
  return response.data.data;
};

/** Applies the collection's About the Study file. Not versioned: it replaces the previous one. */
export const uploadCollectionAbout = async (
  collectionId: string,
  organizationId: string,
  aboutYaml: string,
  aboutFileName: string
): Promise<{ aboutFileName: string | null; updatedAt: string }> => {
  const response = await derinApiClient().put<
    DerinApiResponse<{ aboutFileName: string | null; updatedAt: string }>
  >(
    `/collection/${collectionId}/about-file`,
    { about_yaml: aboutYaml, about_file_name: aboutFileName },
    { params: { organization_id: organizationId } }
  );
  return response.data.data;
};

/** Removes the collection's About the Study file, leaving the card with none. */
export const deleteCollectionAbout = async (
  collectionId: string,
  organizationId: string
): Promise<{ collectionId: string; deleted: true }> => {
  const response = await derinApiClient().delete<
    DerinApiResponse<{ collectionId: string; deleted: true }>
  >(`/collection/${collectionId}/about-file`, {
    params: { organization_id: organizationId },
  });
  return response.data.data;
};

/**
 * Publishes one or more report deliverables to the collection.
 *
 * The only multipart call in the Derin client, and it has to be: the API's body parser is
 * on its 100KB default, so a base64 payload would fail well short of a single slide deck.
 * `Content-Type` is deliberately left unset so the browser writes the multipart boundary.
 *
 * `titles` repeats once per file and is aligned with `files` by index — a blank entry
 * tells the API to fall back to the file name without its extension.
 */
export const uploadCollectionReports = async (
  collectionId: string,
  organizationId: string,
  reports: { file: File; title: string }[]
): Promise<CollectionReport[]> => {
  const form = new FormData();
  reports.forEach(({ file, title }) => {
    form.append("files", file);
    form.append("titles", title);
  });

  const response = await derinApiClient().post<DerinApiResponse<CollectionReport[]>>(
    `/collection/${collectionId}/report-files`,
    form,
    { params: { organization_id: organizationId } }
  );
  return response.data.data;
};

/** Removes a report — the stored file and its listing entry together. */
export const deleteCollectionReport = async (
  collectionId: string,
  organizationId: string,
  reportId: string
): Promise<{ id: string; deleted: true }> => {
  const response = await derinApiClient().delete<
    DerinApiResponse<{ id: string; deleted: true }>
  >(`/collection/${collectionId}/report-files/${encodeURIComponent(reportId)}`, {
    params: { organization_id: organizationId },
  });
  return response.data.data;
};
